import { c as _c } from "react/compiler-runtime";
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { Button } from '@/components/Atoms/Button/Button';
import { Icon, IconSize } from '@/components/Atoms/Icon/Icon';
import { LocalizedDate } from '@/components/Atoms/LocalizedDate/LocalizedDate';
import { LocalizedDateRange } from '@/components/Atoms/LocalizedDateRange/LocalizedDateRange';
import { Prose } from '@/components/Atoms/Prose/Prose';
import { RatioBox } from '@/components/Atoms/RatioBox/RatioBox';
import { H4 } from '@/components/Core/Heading';
import File from '@/components/Icons/File';
import Play from '@/components/Icons/Play';
import Popout from '@/components/Icons/Popout';
import { BVideoPlayer, IdType } from '@/components/Molecules/BVideoPlayer/BVideoPlayer';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { useDialog } from '@/components/Molecules/Dialog/Dialog';
import { EventLiveIndicator } from '@/components/Molecules/EventLiveIndicator/EventLiveIndicator';
import { EventMetadata } from '@/components/Molecules/EventMetadata/EventMetadata';
import type { LimitMessageKey } from '@/components/Molecules/LimitReachedDialog/LimitReachedDialog';
import { LimitReachedDialog } from '@/components/Molecules/LimitReachedDialog/LimitReachedDialog';
import { OfflineDownloadButton } from '@/components/Molecules/OfflineDownloadButton/OfflineDownloadButton';
import { RelatedSidebar, sidebarContentCardRenderer } from '@/components/Molecules/RelatedSidebar/RelatedSidebar';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { VimeoPlayer } from '@/components/Molecules/VimeoPlayer/VimeoPlayer';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentItemCard } from '@/components/Organisms/ContentItemCard/ContentItemCard';
import { Roles } from '@/components/Organisms/Event/SessionDetail/Roles';
import type { PageHeader } from '@/components/Organisms/PageHeader/PageHeader';
import { ShareLinkButton } from '@/components/Organisms/ShareLinkButton/ShareLinkButton';
import { eventsBreadcrumbs } from '@/components/Pages/Events/EventsPage';
import { ArticlePageAside, ArticlePageMain, ArticlePageTemplate } from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { useAppContext } from '@/contexts/AppContext/AppContext';
import useTranslation from '@/hooks/useTranslation';
import type { IContentItem, IEvent, IEventSession, IRole } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import FileExcelIcon from '@bloomberg/web-icons-react/FileExcel';
import type { ShareAction } from 'bnef';
import Trans from 'next-translate/Trans';
import type { ComponentProps, FC, MouseEvent, ReactNode } from 'react';
import type { FCl } from 'react-fc';
import type { KeyedAttachments } from '../../Pages/Insights/useInsightExecSumActions';
import styles from './EventSessionTemplate.module.css';
interface IProps {
  event: IEvent;
  session: IEventSession;
  attachments?: KeyedAttachments;
  people?: IRole[];
  related?: IContentItem[];
  moreTitle?: string;
  shareToken?: null | ShareAction;
  breadcrumbsOverride?: ComponentProps<typeof PageHeader>['breadCrumbs'];
}
export const EventSessionTemplate: FC<IProps> = props => {
  const $ = _c(58);
  const {
    breadcrumbsOverride,
    event,
    session,
    related,
    people,
    moreTitle
  } = props;
  const {
    t
  } = useTranslation("events");
  const actions = useSessionActions(props);
  let breadcrumbs;
  if ($[0] !== event.title || $[1] !== event.url || $[2] !== session.url || $[3] !== session.videoResourceId || $[4] !== t) {
    breadcrumbs = [...eventsBreadcrumbs(t)];
    if (session.videoResourceId) {
      let t0;
      if ($[6] !== t) {
        t0 = t("events:OnDemand");
        $[6] = t;
        $[7] = t0;
      } else {
        t0 = $[7];
      }
      let t1;
      if ($[8] !== t0) {
        t1 = {
          label: t0,
          url: "/events/past"
        };
        $[8] = t0;
        $[9] = t1;
      } else {
        t1 = $[9];
      }
      breadcrumbs.push(t1);
    }
    if (session.url !== event.url) {
      let t0;
      if ($[10] !== event.title || $[11] !== event.url) {
        t0 = {
          label: event.title,
          url: event.url
        };
        $[10] = event.title;
        $[11] = event.url;
        $[12] = t0;
      } else {
        t0 = $[12];
      }
      breadcrumbs.push(t0);
    }
    $[0] = event.title;
    $[1] = event.url;
    $[2] = session.url;
    $[3] = session.videoResourceId;
    $[4] = t;
    $[5] = breadcrumbs;
  } else {
    breadcrumbs = $[5];
  }
  const t0 = breadcrumbsOverride ?? breadcrumbs;
  let t1;
  if ($[13] !== session.event.subtype || $[14] !== session.type) {
    t1 = <ContentTypeHeader type={session.type} eventSubtype={session.event.subtype} />;
    $[13] = session.event.subtype;
    $[14] = session.type;
    $[15] = t1;
  } else {
    t1 = $[15];
  }
  let t2;
  if ($[16] !== session.event) {
    t2 = <EventLiveIndicator event={session.event} />;
    $[16] = session.event;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== t1 || $[19] !== t2) {
    t3 = <BadgeList>{t1}{t2}</BadgeList>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const t4 = session.videoResourceId ? session.displayDate : undefined;
  let t5;
  if ($[21] !== actions || $[22] !== session.title || $[23] !== t0 || $[24] !== t3 || $[25] !== t4) {
    t5 = <ArticlePageHeader breadCrumbs={t0} title={session.title} status={t3} date={t4} actions={actions} />;
    $[21] = actions;
    $[22] = session.title;
    $[23] = t0;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== event || $[28] !== session) {
    t6 = session.videoResourceId ? <Video className="prose-column--full" session={session} /> : <DetailPanel event={event} />;
    $[27] = event;
    $[28] = session;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== session.description || $[31] !== t) {
    t7 = session.description && <Prose $preformatted={true}><H4 as="h2">{t("SessionDetail.aboutEvent")}</H4><p>{session.description}</p></Prose>;
    $[30] = session.description;
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== props) {
    t8 = <DateLine {...props} />;
    $[33] = props;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== session.subjectInsight) {
    t9 = session.subjectInsight && <ContentItemCard item={session.subjectInsight} />;
    $[35] = session.subjectInsight;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== t6 || $[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t10 = <ArticlePageMain>{t6}{t7}{t8}{t9}</ArticlePageMain>;
    $[37] = t6;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== people) {
    t11 = <Roles people={people} />;
    $[42] = people;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== moreTitle || $[45] !== related || $[46] !== session.id || $[47] !== t) {
    t12 = related?.length > 0 && <RelatedSidebar lists={[{
      title: moreTitle ?? t("More.from_publicwebinar"),
      items: related,
      itemRenderer: sidebarContentCardRenderer(`Related: Video: ${session.id}`)
    }]} />;
    $[44] = moreTitle;
    $[45] = related;
    $[46] = session.id;
    $[47] = t;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t11 || $[50] !== t12) {
    t13 = <ArticlePageAside>{t11}{t12}</ArticlePageAside>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t10 || $[53] !== t13) {
    t14 = <ArticlePageTemplate>{t10}{t13}</ArticlePageTemplate>;
    $[52] = t10;
    $[53] = t13;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== t14 || $[56] !== t5) {
    t15 = <div>{t5}{t14}</div>;
    $[55] = t14;
    $[56] = t5;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  return t15;
};
const useSessionActions = t0 => {
  const $ = _c(47);
  const {
    attachments,
    session,
    event,
    shareToken
  } = t0;
  const {
    t
  } = useTranslation("events");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== event.endDate) {
    t2 = new Date(event.endDate);
    $[1] = event.endDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hasEnded = t1 > t2;
  const t3 = `Event: ${session?.event?.id ?? ""}: Session ${session.id}`;
  let t4;
  if ($[3] !== session.id || $[4] !== session.title || $[5] !== t3) {
    t4 = <SaveButton key="save-button" id={session.id} type="eventsession" itemName={session.title} analyticsBaseId={t3} />;
    $[3] = session.id;
    $[4] = session.title;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let actions;
  if ($[7] !== attachments || $[8] !== event.registrationUrl || $[9] !== hasEnded || $[10] !== session.id || $[11] !== session.title || $[12] !== session.url || $[13] !== shareToken || $[14] !== t || $[15] !== t4) {
    actions = [t4];
    if (attachments?.pdf) {
      const t5 = `${session.url}/slides`;
      let t6;
      if ($[17] !== attachments.pdf.url || $[18] !== session.id || $[19] !== session.title || $[20] !== t5) {
        t6 = <OfflineDownloadButton key="offline" contentType={ContentType.eventsession} itemId={session.id} title={session.title} viewerUrl={t5} pdfUrl={attachments.pdf.url} />;
        $[17] = attachments.pdf.url;
        $[18] = session.id;
        $[19] = session.title;
        $[20] = t5;
        $[21] = t6;
      } else {
        t6 = $[21];
      }
      actions.push(t6);
    }
    if (shareToken && attachments?.pdf) {
      let t5;
      if ($[22] !== shareToken.url) {
        t5 = async () => shareToken.url;
        $[22] = shareToken.url;
        $[23] = t5;
      } else {
        t5 = $[23];
      }
      let t6;
      if ($[24] !== shareToken.remainingShares || $[25] !== t5) {
        t6 = <ShareLinkButton key="share" linkFetcher={t5} shareTokenRequired={true} shares={shareToken.remainingShares} />;
        $[24] = shareToken.remainingShares;
        $[25] = t5;
        $[26] = t6;
      } else {
        t6 = $[26];
      }
      actions.push(t6);
    }
    if (attachments?.excel) {
      const t5 = attachments.excel.url;
      let t6;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <FileExcelIcon />;
        $[27] = t6;
      } else {
        t6 = $[27];
      }
      const t7 = attachments.excel.withinLimit;
      let t8;
      if ($[28] !== t) {
        t8 = t("common:ResourceType.data");
        $[28] = t;
        $[29] = t8;
      } else {
        t8 = $[29];
      }
      let t9;
      if ($[30] !== attachments.excel.url || $[31] !== attachments.excel.withinLimit || $[32] !== t8) {
        t9 = <SessionActionWithDialog key="excel" href={t5} icon={t6} kind="tertiary" withinLimit={t7} limitMessageKey="data">{t8}</SessionActionWithDialog>;
        $[30] = attachments.excel.url;
        $[31] = attachments.excel.withinLimit;
        $[32] = t8;
        $[33] = t9;
      } else {
        t9 = $[33];
      }
      actions.push(t9);
    }
    if (attachments?.pdf) {
      const t5 = `${session.url}/slides`;
      let t6;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <File />;
        $[34] = t6;
      } else {
        t6 = $[34];
      }
      const t7 = attachments.pdf.withinLimit;
      let t8;
      if ($[35] !== t) {
        t8 = t("common:Actions.view_slides");
        $[35] = t;
        $[36] = t8;
      } else {
        t8 = $[36];
      }
      let t9;
      if ($[37] !== attachments.pdf.withinLimit || $[38] !== t5 || $[39] !== t8) {
        t9 = <SessionActionWithDialog key="pdf" href={t5} icon={t6} kind="primary" withinLimit={t7} limitMessageKey="insight">{t8}</SessionActionWithDialog>;
        $[37] = attachments.pdf.withinLimit;
        $[38] = t5;
        $[39] = t8;
        $[40] = t9;
      } else {
        t9 = $[40];
      }
      actions.push(t9);
    }
    if (!hasEnded && event.registrationUrl) {
      const t5 = event.registrationUrl;
      let t6;
      if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <Popout />;
        $[41] = t6;
      } else {
        t6 = $[41];
      }
      let t7;
      if ($[42] !== t) {
        t7 = t("common:Event.Registration.registration");
        $[42] = t;
        $[43] = t7;
      } else {
        t7 = $[43];
      }
      let t8;
      if ($[44] !== event.registrationUrl || $[45] !== t7) {
        t8 = <Button key="register" href={t5} icon={t6} kind="primary" target="_blank" rel="noopener noreferrer">{t7}</Button>;
        $[44] = event.registrationUrl;
        $[45] = t7;
        $[46] = t8;
      } else {
        t8 = $[46];
      }
      actions.push(t8);
    }
    $[7] = attachments;
    $[8] = event.registrationUrl;
    $[9] = hasEnded;
    $[10] = session.id;
    $[11] = session.title;
    $[12] = session.url;
    $[13] = shareToken;
    $[14] = t;
    $[15] = t4;
    $[16] = actions;
  } else {
    actions = $[16];
  }
  return actions;
};
const SessionActionWithDialog = t0 => {
  const $ = _c(16);
  const {
    href,
    icon,
    kind,
    withinLimit,
    limitMessageKey,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      lazy: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const dialog = useDialog(t1);
  let onClick;
  if (!withinLimit) {
    let t2;
    if ($[1] !== dialog) {
      t2 = evt => {
        evt.preventDefault();
        dialog.open();
        return false;
      };
      $[1] = dialog;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    onClick = t2;
  }
  let t2;
  if ($[3] !== children || $[4] !== href || $[5] !== icon || $[6] !== kind || $[7] !== onClick) {
    t2 = <Button href={href} icon={icon} kind={kind} onClick={onClick}>{children}</Button>;
    $[3] = children;
    $[4] = href;
    $[5] = icon;
    $[6] = kind;
    $[7] = onClick;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== dialog || $[10] !== limitMessageKey || $[11] !== withinLimit) {
    t3 = !withinLimit && <LimitReachedDialog dialog={dialog} limitMessageKey={limitMessageKey} />;
    $[9] = dialog;
    $[10] = limitMessageKey;
    $[11] = withinLimit;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
const DetailPanel: FC<{
  event: IProps['event'];
}> = t0 => {
  const $ = _c(2);
  const {
    event
  } = t0;
  let t1;
  if ($[0] !== event) {
    t1 = <EventMetadata className="cds-t-body-xlarge" event={event} />;
    $[0] = event;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const Video: FCl<{
  session: IProps['session'];
}> = t0 => {
  const $ = _c(19);
  const {
    session,
    className
  } = t0;
  const {
    userInChina
  } = useAppContext();
  const showPolyvLink = userInChina && session.videoPolyvId;
  if (!session.videoResourceId) {
    return null;
  }
  let t1;
  if ($[0] !== session.leadImage?.baseUrl || $[1] !== session.videoPolyvId || $[2] !== showPolyvLink) {
    t1 = showPolyvLink && <a className={styles.polyvVideoLink} style={{
      backgroundImage: `url(${session.leadImage?.baseUrl})`
    }} href={`https://live.polyv.cn/watch/${session.videoPolyvId}`} target="_blank" rel="noopener" data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, TrackingCategory.PolyvVideo, session.videoPolyvId)}><Icon use={<Play />} size={IconSize.LARGE} /></a>;
    $[0] = session.leadImage?.baseUrl;
    $[1] = session.videoPolyvId;
    $[2] = showPolyvLink;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== session.videoResourceId || $[5] !== session.videoResourceType || $[6] !== showPolyvLink) {
    t2 = !showPolyvLink && session.videoResourceType === "bloomberg" && <BVideoPlayer videoId={session.videoResourceId} videoIdType={IdType.BMMR} onFirstPlay={() => trackEvent(TrackingAction.Play, TrackingCategory.BloombergVideo, session.videoResourceId)} />;
    $[4] = session.videoResourceId;
    $[5] = session.videoResourceType;
    $[6] = showPolyvLink;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== session.videoResourceId || $[9] !== session.videoResourceType || $[10] !== showPolyvLink) {
    t3 = !showPolyvLink && session.videoResourceType === "vimeo" && <VimeoPlayer videoId={session.videoResourceId} />;
    $[8] = session.videoResourceId;
    $[9] = session.videoResourceType;
    $[10] = showPolyvLink;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <RatioBox ratio={1.7777777777777777}>{t1}{t2}{t3}</RatioBox>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== className || $[17] !== t4) {
    t5 = <div className={className}>{t4}</div>;
    $[16] = className;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
const DateLine: FC<IProps> = t0 => {
  const $ = _c(17);
  const {
    session,
    event
  } = t0;
  if (!session.videoResourceId) {
    return null;
  }
  let dateText;
  bb0: switch (session.type) {
    case ContentType.forum:
    case ContentType.summit:
      {
        let t1;
        if ($[0] !== event.title || $[1] !== event.url) {
          t1 = <Trans i18nKey="events:SessionDetail.eventContext" components={{
            eventTitle: <a href={event.url}>{event.title}</a>
          }} />;
          $[0] = event.title;
          $[1] = event.url;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        let t2;
        if ($[3] !== event.endDate || $[4] !== event.startDate) {
          t2 = <LocalizedDateRange startDate={event.startDate} endDate={event.endDate} />;
          $[3] = event.endDate;
          $[4] = event.startDate;
          $[5] = t2;
        } else {
          t2 = $[5];
        }
        const t3 = event.location ? `, ${event.location}` : "";
        let t4;
        if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
          t4 = <>{t1}: {t2}{t3}</>;
          $[6] = t1;
          $[7] = t2;
          $[8] = t3;
          $[9] = t4;
        } else {
          t4 = $[9];
        }
        dateText = t4;
        break bb0;
      }
    default:
      {
        let t1;
        if ($[10] !== event.startDate) {
          t1 = <Trans i18nKey="events:SessionDetail.date" components={{
            date: <LocalizedDate date={event.startDate} />
          }} />;
          $[10] = event.startDate;
          $[11] = t1;
        } else {
          t1 = $[11];
        }
        const t2 = event.location ? `, ${event.location}` : "";
        let t3;
        if ($[12] !== t1 || $[13] !== t2) {
          t3 = <>{t1}{t2}</>;
          $[12] = t1;
          $[13] = t2;
          $[14] = t3;
        } else {
          t3 = $[14];
        }
        dateText = t3;
      }
  }
  let t1;
  if ($[15] !== dateText) {
    t1 = <p>{dateText}</p>;
    $[15] = dateText;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
};